import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatDigits, formatMoney, formatNumber, gregorianToJalali } from "../lib/format";
import { useT } from "../i18n";
import { ConfirmDialog } from "../components/ConfirmDialog";

interface Employee {
  id: string;
  name: string;
  role: string;
  salaryMinor: number;
  active: boolean;
}

interface PayrollRun {
  id: string;
  period: string;
  totalNetMinor: number;
  status: string;
  lines: { name: string; netMinor: number }[];
}

function currentJalaliPeriod(): string {
  const now = new Date();
  const { jy, jm } = gregorianToJalali(now.getFullYear(), now.getMonth() + 1, now.getDate());
  return `${jy}-${String(jm).padStart(2, "0")}`;
}

export function HrPage() {
  const { orgId } = useSession();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [salaryAfn, setSalaryAfn] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Payroll run awaiting the owner's "yes" before it is paid and posted.
  const [toPost, setToPost] = useState<PayrollRun | null>(null);
  const t = useT();

  useEffect(() => {
    if (!orgId) return;
    const unsubEmployees = onSnapshot(
      query(collection(db, "organizations", orgId, "employees"), orderBy("name")),
      (snap) =>
        setEmployees(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Employee, "id">) })),
        ),
    );
    const unsubRuns = onSnapshot(
      query(collection(db, "organizations", orgId, "payrollRuns"), orderBy("period", "desc")),
      (snap) =>
        setRuns(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<PayrollRun, "id">) }))),
    );
    return () => {
      unsubEmployees();
      unsubRuns();
    };
  }, [orgId]);

  async function addEmployee() {
    setBusy(true);
    try {
      await api(`/orgs/${orgId}/hr/employees`, {
        method: "POST",
        body: {
          name,
          role,
          salaryMinor: Math.round(Number(salaryAfn) * 100),
          salaryCurrency: "AFN",
          hireDate: new Date().toISOString().slice(0, 10),
        },
      });
      setToast(t("hr_toast_employee_saved"));
      setAddOpen(false);
      setName("");
      setRole("");
      setSalaryAfn("");
    } catch (e) {
      setToast(e instanceof Error ? e.message : t("common_error"));
    } finally {
      setBusy(false);
    }
  }

  async function attendance(employeeId: string) {
    try {
      const result = await api<{ state: string }>(`/orgs/${orgId}/hr/attendance/check`, {
        method: "POST",
        body: { employeeId },
      });
      setToast(result.state === "checked_in" ? t("hr_toast_checked_in") : t("hr_toast_checked_out"));
    } catch (e) {
      setToast(e instanceof Error ? e.message : t("common_error"));
    }
  }

  async function createRun() {
    setBusy(true);
    try {
      const result = await api<{ lineCount: number; totalNetMinor: number }>(
        `/orgs/${orgId}/hr/payroll/runs`,
        { method: "POST", body: { period: currentJalaliPeriod() } },
      );
      setToast(
        t("hr_toast_payroll_created", {
          period: formatDigits(currentJalaliPeriod()),
          count: result.lineCount,
          total: formatMoney(result.totalNetMinor),
        }),
      );
    } catch (e) {
      setToast(e instanceof Error ? e.message : t("common_error"));
    } finally {
      setBusy(false);
    }
  }

  async function postRun(runId: string) {
    setBusy(true);
    try {
      await api(`/orgs/${orgId}/hr/payroll/runs/${runId}/post`, { method: "POST" });
      setToast(t("hr_toast_payroll_posted"));
    } catch (e) {
      setToast(e instanceof Error ? e.message : t("common_error"));
    } finally {
      setBusy(false);
      setToPost(null);
    }
  }

  return (
    <>
      <Stack direction="row" alignItems="center" mb={2} spacing={2}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          {t("hr_title")}
        </Typography>
        <Button variant="outlined" startIcon={<PersonAddAltOutlinedIcon />} onClick={() => setAddOpen(true)}>
          {t("hr_new_employee")}
        </Button>
        <Button variant="contained" onClick={createRun} disabled={busy || employees.length === 0}>
          {t("hr_create_payroll", { period: formatDigits(currentJalaliPeriod()) })}
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("hr_col_name")}</TableCell>
              <TableCell>{t("hr_col_role")}</TableCell>
              <TableCell>{t("hr_col_salary")}</TableCell>
              <TableCell>{t("hr_col_today_attendance")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((employee) => (
              <TableRow key={employee.id} hover>
                <TableCell>{employee.name}</TableCell>
                <TableCell>{employee.role}</TableCell>
                <TableCell>{formatMoney(employee.salaryMinor)}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => attendance(employee.id)}>
                    {t("hr_check_inout")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {employees.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    {t("hr_empty_employees")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6" mb={1}>
        {t("hr_payroll_runs_title")}
      </Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("hr_col_period")}</TableCell>
              <TableCell>{t("hr_col_employees")}</TableCell>
              <TableCell>{t("hr_col_net_total")}</TableCell>
              <TableCell>{t("common_status")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run) => (
              <TableRow key={run.id} hover>
                <TableCell>{formatDigits(run.period)}</TableCell>
                <TableCell>{formatNumber(run.lines?.length ?? 0)}</TableCell>
                <TableCell>{formatMoney(run.totalNetMinor)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={run.status === "posted" ? "success" : "warning"}
                    label={run.status === "posted" ? t("hr_run_status_posted") : t("hr_run_status_draft")}
                  />
                </TableCell>
                <TableCell align="right">
                  {run.status === "draft" && (
                    <Button size="small" variant="contained" disabled={busy} onClick={() => setToPost(run)}>
                      {t("hr_post_run")}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    {t("hr_empty_runs")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("hr_dialog_title")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label={t("hr_full_name")} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            <TextField label={t("hr_col_role")} value={role} onChange={(e) => setRole(e.target.value)} />
            <TextField
              label={t("hr_salary_label")}
              type="number"
              value={salaryAfn}
              onChange={(e) => setSalaryAfn(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>{t("common_cancel")}</Button>
          <Button
            variant="contained"
            onClick={addEmployee}
            disabled={busy || name.trim().length < 2 || role.trim().length < 2 || Number(salaryAfn) <= 0}
          >
            {t("common_submit")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}>
        <Alert onClose={() => setToast(null)} closeText={t("common_close")} severity="info">
          {toast}
        </Alert>
      </Snackbar>
      <ConfirmDialog
        open={toPost !== null}
        title={t("confirm_payroll_title")}
        body={
          toPost
            ? t("confirm_payroll_body", {
                period: formatDigits(toPost.period),
                total: formatMoney(toPost.totalNetMinor),
              })
            : ""
        }
        note={t("confirm_ledger_note")}
        confirmLabel={t("hr_post_run")}
        busy={busy}
        onConfirm={() => toPost && postRun(toPost.id)}
        onClose={() => setToPost(null)}
      />
    </>
  );
}
